import logger from '../utils/logger';

const STORAGE_KEY = 'x-correlation-id';
const HEADER_NAME = 'X-Correlation-ID';

export interface ICorrelationService {
  /**
   * Retrieves the current correlation token from session storage
   * @returns The correlation token or null if not present
   */
  get(): string | null;

  /**
   * Stores a correlation token in session storage
   * @param token - The correlation token to store
   */
  set(token: string): void;

  /**
   * Extracts correlation token from response headers and stores it
   * @param response - The fetch Response object
   * @returns The extracted token or null if not present
   */
  extractFromResponse(response: Response): string | null;

  /**
   * Removes the correlation token from session storage
   */
  clear(): void;
}

class CorrelationService implements ICorrelationService {
  /**
   * Checks if session storage is available
   */
  private isStorageAvailable(): boolean {
    try {
      const testKey = '__storage_test__';
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Retrieves the current correlation token from session storage
   */
  public get(): string | null {
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch (error) {
      logger.error('Failed to retrieve correlation token from session storage', {
        error: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
      return null;
    }
  }

  /**
   * Stores a correlation token in session storage
   */
  public set(token: string): void {
    // Skip storage for null, undefined, or empty tokens
    if (!token || token.trim() === '') {
      return;
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, token);
    } catch (error) {
      logger.error('Failed to store correlation token in session storage', {
        error: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
    }
  }

  /**
   * Extracts correlation token from response headers and stores it
   */
  public extractFromResponse(response: Response): string | null {
    try {
      // Extract token from response headers (case-insensitive)
      const token = response.headers.get(HEADER_NAME);

      if (token && token.trim() !== '') {
        this.set(token);
        return token;
      }

      this.clear();
      return null;
    } catch (error) {
      logger.error('Failed to extract correlation token from response', {
        url: response.url,
        statusCode: response.status,
        error: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
      return null;
    }
  }

  /**
   * Removes the correlation token from session storage
   */
  public clear(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      logger.error('Failed to clear correlation token from session storage', {
        error: error instanceof Error ? error.message : String(error),
        stackTrace: error instanceof Error ? error.stack : undefined,
      });
    }
  }
}

// Export singleton instance
export const correlationService = new CorrelationService();
